import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import InputMask from 'react-input-mask';
import styled from 'styled-components';
import { rem } from '../../utils/functions.js';
import { ADORNMENT_POSITION, INPUT_VARIANT } from './constants.js';
import { typography } from '../../typography/typography.js';

var _excluded = ["forceUpper", "icon", "adornment", "adornmentPosition", "error", "isPassword", "mask", "maskChar", "formatChars", "textAlign", "inputStyle", "theme"];
// helper function for determining focus color

var focusColor = function focusColor(focused, error, theme) {
  var borderColor;

  if (error) {
    borderColor = theme.colorPalette.utilityColors.error;
  } else if (focused) {
    borderColor = theme.colorPalette.primaryColors.main.shade_default;
  } else {
    borderColor = 'inherit';
  }

  return borderColor;
}; // helper function for determining hover color


var hoverColor = function hoverColor(error, theme) {
  var borderColor;

  if (error) {
    borderColor = theme.colorPalette.utilityColors.error;
  } else {
    borderColor = theme.colorPalette.primaryColors.main.shade_default;
  }

  return borderColor;
};

var Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__Root",
  componentId: "sc-k03yft-0"
})(["position:relative;width:295px;", ""], function (_ref) {
  var rootStyle = _ref.rootStyle;
  return rootStyle;
});
process.env.NODE_ENV !== "production" ? Root.displayName = 'Root' : void 0;
var TooltipContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__TooltipContainer",
  componentId: "sc-k03yft-1"
})(["padding-left:6px;"]);
process.env.NODE_ENV !== "production" ? TooltipContainer.displayName = 'TooltipContainer' : void 0;
var FlexBox = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__FlexBox",
  componentId: "sc-k03yft-2"
})(["display:inline-flex;flex-direction:column;width:100%;", ""], function (_ref2) {
  var inputFlexWrapperStyle = _ref2.inputFlexWrapperStyle;
  return inputFlexWrapperStyle;
});
process.env.NODE_ENV !== "production" ? FlexBox.displayName = 'FlexBox' : void 0;
var LabelWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__LabelWrapper",
  componentId: "sc-k03yft-3"
})(["display:inline-flex;flex-direction:row;align-items:baseline;justify-content:space-between;width:100%;"]);
process.env.NODE_ENV !== "production" ? LabelWrapper.displayName = 'LabelWrapper' : void 0;
var LabelTooltipWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__LabelTooltipWrapper",
  componentId: "sc-k03yft-4"
})(["display:inline-flex;flex-direction:row;align-items:baseline;"]);
var InputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__InputWrapper",
  componentId: "sc-k03yft-5"
})(["position:relative;box-sizing:border-box;line-height:", ";height:", ";display:flex;align-items:center;background-color:", ";border-style:solid;border-color:", ";border-width:", ";&:hover{border-color:", ";}&:before{left:0;right:0;bottom:0;z-index:1;content:'';position:absolute;transition:border-bottom-color 200ms cubic-bezier(0.4,0,0.2,1) 0ms;border-bottom:0px solid ", ";border-bottom-color:", ";pointer-events:none;}&:after{", "}", ""], rem(48), rem(48), function (_ref3) {
  var variant = _ref3.variant,
      theme = _ref3.theme;

  if (variant === INPUT_VARIANT.BASIC_LINE) {
    return 'transparent';
  }

  return theme.colorPalette.neutralColors.light.shade_white;
}, function (_ref4) {
  var error = _ref4.error,
      focused = _ref4.focused,
      theme = _ref4.theme;

  if (error) {
    return theme.colorPalette.utilityColors.error;
  }

  if (focused) {
    return theme.colorPalette.primaryColors.main.shade_default;
  }

  return theme.colorPalette.neutralColors.light.shade_dark_3;
}, function (_ref5) {
  var variant = _ref5.variant,
      error = _ref5.error;

  if (error) {
    if (variant === INPUT_VARIANT.BASIC_LINE) {
      return '0 0 2px 0';
    }

    return '2px';
  }

  if (variant === INPUT_VARIANT.BASIC_LINE) {
    return '0 0 1px 0';
  }

  return '1px';
}, function (_ref6) {
  var error = _ref6.error,
      theme = _ref6.theme;
  return hoverColor(error, theme);
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_1;
}, function (_ref8) {
  var focused = _ref8.focused,
      error = _ref8.error,
      theme = _ref8.theme;
  return focusColor(focused, error, theme);
}, function (_ref9) {
  var variant = _ref9.variant,
      focused = _ref9.focused,
      error = _ref9.error,
      theme = _ref9.theme;

  if (variant === INPUT_VARIANT.BASIC_LINE) {
    return "\n      left: 0;\n      right: 0;\n      bottom: 0;\n      content: '';\n      position: absolute;\n      z-index: 2;\n      transform: ".concat(focused ? 'scaleX(1)' : 'scaleX(0)', ";\n      transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;\n      border-bottom: 1px solid ").concat(theme.colorPalette.neutralColors.dark.shade_light_1, ";\n      border-bottom-color: ").concat(focusColor(focused, error, theme), ";\n      pointer-events: none;\n    ");
  }

  return '';
}, function (_ref10) {
  var inputWrapperStyle = _ref10.inputWrapperStyle;
  return inputWrapperStyle;
});
process.env.NODE_ENV !== "production" ? InputWrapper.displayName = 'InputWrapper' : void 0;
var DisabledInputWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "styledComponents__DisabledInputWrapper",
  componentId: "sc-k03yft-6"
})(["position:relative;box-sizing:border-box;line-height:", ";height:", ";display:flex;align-items:center;background-color:", ";border-style:dotted;border-color:", ";border-width:", ";", ""], rem(48), rem(48), function (_ref11) {
  var theme = _ref11.theme;
  return theme.colorPalette.neutralColors.light.shade_default;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_4;
}, function (_ref13) {
  var variant = _ref13.variant;
  return variant === INPUT_VARIANT.BASIC_LINE ? '0 0 1px 0' : '1px';
}, function (_ref14) {
  var inputWrapperStyle = _ref14.inputWrapperStyle;
  return inputWrapperStyle;
});
process.env.NODE_ENV !== "production" ? DisabledInputWrapper.displayName = 'DisabledInputWrapper' : void 0;
/* eslint-disable react/prop-types */

var Input = /*#__PURE__*/styled( /*#__PURE__*/React.forwardRef(function (_ref15, ref) {
  _ref15.forceUpper;
      _ref15.icon;
      _ref15.adornment;
      _ref15.adornmentPosition;
      _ref15.error;
      _ref15.isPassword;
      var mask = _ref15.mask,
      maskChar = _ref15.maskChar,
      formatChars = _ref15.formatChars;
      _ref15.textAlign;
      _ref15.inputStyle;
      _ref15.theme;
      var rest = _objectWithoutProperties(_ref15, _excluded);

  return mask ? /*#__PURE__*/React.createElement(InputMask, _extends({}, rest, {
    inputRef: ref,
    mask: mask,
    maskChar: maskChar,
    formatChars: formatChars
  })) : /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    ref: ref
  }));
})).withConfig({
  displayName: "styledComponents__Input",
  componentId: "sc-k03yft-7"
})(["font-family:", ";font-size:", ";position:relative;font-weight:300;line-height:", ";color:", ";background-color:transparent;width:100%;box-sizing:border-box;border:none;padding-left:", ";padding-right:", ";height:", ";text-align:", ";text-transform:", ";&:disabled{color:", ";border-bottom-style:none;}::placeholder{color:", ";text-transform:initial;}::-ms-reveal,::-ms-clear{display:none;}", ""], typography.fontFamily, typography.fontSize, rem(48), function (_ref16) {
  var theme = _ref16.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_1;
}, function (_ref17) {
  var adornment = _ref17.adornment,
      adornmentPosition = _ref17.adornmentPosition;
  return adornment === '' || adornmentPosition === ADORNMENT_POSITION.RIGHT ? rem(12) : rem(36);
}, function (_ref18) {
  var icon = _ref18.icon,
      adornment = _ref18.adornment,
      adornmentPosition = _ref18.adornmentPosition,
      isPassword = _ref18.isPassword;

  if (adornment !== '' && adornmentPosition === ADORNMENT_POSITION.RIGHT) {
    return rem(60);
  }

  if (isPassword) {
    return rem(77);
  }

  return icon ? rem(48) : rem(12);
}, rem(48), function (_ref19) {
  var textAlign = _ref19.textAlign;
  return textAlign;
}, function (_ref20) {
  var forceUpper = _ref20.forceUpper;
  return forceUpper ? 'uppercase' : 'none';
}, function (_ref21) {
  var theme = _ref21.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_3;
}, function (_ref22) {
  var theme = _ref22.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_4;
}, function (_ref23) {
  var inputStyle = _ref23.inputStyle;
  return inputStyle;
});
process.env.NODE_ENV !== "production" ? Input.displayName = 'Input' : void 0;
/* eslint-enable react/prop-types */

var InputIcon = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__InputIcon",
  componentId: "sc-k03yft-8"
})(["display:flex;align-items:center;justify-content:center;justify-content:", ";position:absolute;right:0;line-height:", ";width:", ";pointer-events:", ";"], function (_ref24) {
  var variant = _ref24.variant;
  return variant === INPUT_VARIANT.BASIC_LINE ? 'end' : 'center';
}, rem(48), rem(48), function (_ref25) {
  var isIconClickable = _ref25.isIconClickable;
  return isIconClickable ? 'auto' : 'none';
});
process.env.NODE_ENV !== "production" ? InputIcon.displayName = 'InputIcon' : void 0; // Styled components for InputAdornment

var InputAdornmentWrapper = /*#__PURE__*/styled.p.withConfig({
  displayName: "styledComponents__InputAdornmentWrapper",
  componentId: "sc-k03yft-9"
})(["font-family:", ";font-size:", ";font-weight:300;position:absolute;display:flex;align-items:center;justify-content:", ";line-height:", ";margin:0;width:", ";right:", ";left:", ";pointer-events:none;", ""], typography.fontFamily, typography.fontSize, function (_ref26) {
  var position = _ref26.position,
      variant = _ref26.variant;

  if (variant === INPUT_VARIANT.BASIC_LINE) {
    return position === ADORNMENT_POSITION.LEFT ? 'start' : 'end';
  }

  return 'center';
}, rem(48), rem(36), function (_ref27) {
  var position = _ref27.position,
      hasIcon = _ref27.hasIcon;

  if (position === ADORNMENT_POSITION.RIGHT) {
    if (hasIcon) {
      return rem(36);
    }

    return 0;
  }

  return 'auto';
}, function (_ref28) {
  var position = _ref28.position;
  return position === ADORNMENT_POSITION.LEFT ? 0 : 'auto';
}, function (_ref29) {
  var adornmentStyle = _ref29.adornmentStyle;
  return adornmentStyle;
});
process.env.NODE_ENV !== "production" ? InputAdornmentWrapper.displayName = 'InputAdornmentWrapper' : void 0; // Styled components for ShowPasswordToggle

var ShowPasswordToggleWrapper = /*#__PURE__*/styled.button.withConfig({
  displayName: "styledComponents__ShowPasswordToggleWrapper",
  componentId: "sc-k03yft-10"
})(["display:flex;align-items:center;justify-content:center;min-width:", ";padding:0;position:absolute;right:0;background:transparent;border:none;cursor:pointer;color:", ";font-family:", ";font-weight:", ";font-size:", ";:hover,:focus{text-decoration:none;color:", ";}"], rem(77), function (_ref30) {
  var theme = _ref30.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_3;
}, typography.fontFamily, typography.fontWeightDemibold, typography.fontSizeSmall, function (_ref31) {
  var theme = _ref31.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_1;
});
process.env.NODE_ENV !== "production" ? ShowPasswordToggleWrapper.displayName = 'ShowPasswordToggleWrapper' : void 0;
var OptionalText = /*#__PURE__*/styled.span.withConfig({
  displayName: "styledComponents__OptionalText",
  componentId: "sc-k03yft-11"
})(["font-family:", ";font-size:", ";font-weight:", ";color:", ";line-height:", ";text-transform:uppercase;"], typography.fontFamily, typography.fontSizeSmall, typography.fontWeightDemibold, function (_ref32) {
  var theme = _ref32.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_3;
}, typography.lineHeight);
process.env.NODE_ENV !== "production" ? OptionalText.displayName = 'OptionalText' : void 0; // styled component for the datalist

var StyledDatalist = /*#__PURE__*/styled.ul.withConfig({
  displayName: "styledComponents__StyledDatalist",
  componentId: "sc-k03yft-12"
})(["margin:0px;position:relative;display:block;border-width:1px;border-style:solid;border-color:", ";padding-left:0px;white-space:normal;background:", ";color:", ";"], function (_ref33) {
  var theme = _ref33.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_4;
}, function (_ref34) {
  var theme = _ref34.theme;
  return theme.colorPalette.neutralColors.light.shade_white;
}, function (_ref35) {
  var theme = _ref35.theme;
  return theme.colorPalette.neutralColors.dark.shade_light_1;
});
process.env.NODE_ENV !== "production" ? StyledDatalist.displayName = 'StyledDatalist' : void 0;
var DatalistItem = /*#__PURE__*/styled.li.withConfig({
  displayName: "styledComponents__DatalistItem",
  componentId: "sc-k03yft-13"
})(["position:relative;list-style:none;display:block;font-family:", ";font-size:", ";line-height:", ";text-align:left;font-weight:", ";padding-top:10px;padding-bottom:10px;padding-left:20px;white-space:normal;&:hover{font-weight:", ";text-decoration:none;background:", ";}"], typography.fontFamily, typography.fontSize, typography.lineHeight, function (_ref36) {
  var isSelected = _ref36.isSelected;
  return isSelected ? typography.fontWeightDemibold : typography.fontWeightLight;
}, typography.fontWeightDemibold, function (_ref37) {
  var theme = _ref37.theme;
  return theme.colorPalette.neutralColors.light.shade_light_1;
});
process.env.NODE_ENV !== "production" ? DatalistItem.displayName = 'DatalistItem' : void 0;

export { DatalistItem, DisabledInputWrapper, FlexBox, Input, InputAdornmentWrapper, InputIcon, InputWrapper, LabelTooltipWrapper, LabelWrapper, OptionalText, Root, ShowPasswordToggleWrapper, StyledDatalist, TooltipContainer };
